/**
 * OpportunityReadOnlySummary
 * ──────────────────────────
 * Phase 1 — Opportunity Decomposition.
 *
 * Display-only view for opportunities that are NOT a live workspace:
 *   - Closed Lost
 *   - Closed Won with no live linked job (canceled / finance-declined / no link)
 *
 * Renders only fields already stored on the Opportunity record itself.
 * Any number shown here is an audit snapshot (value-at-close), not a
 * canonical computation. Labels make this explicit.
 *
 * Integrity rules for this file are enforced by canonicalSyncGuardrail.test.ts.
 */
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { format } from "date-fns";
import { ArrowLeft, Trophy, Ban, AlertCircle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { resolveProfileDisplayName } from "@/lib/profileDisplayName";
import { createSignedAttachmentUrl, getAttachments } from "@/services/attachment.service";
import type { Opportunity } from "@/data/opportunityTypes";
import { WON_OUTCOME_LABELS } from "@/data/opportunityTypes";
import { StorageThumbnailImage } from "@/components/media/StorageThumbnailImage";

interface Props {
  opportunity: Opportunity & {
    customerName?: string;
    customerAddress?: string;
  };
  /** Optional reason for showing summary instead of redirecting to a job. */
  noJobReason?: "rescission" | "canceled" | "finance_declined" | "no_link";
  embedded?: boolean;
}

const STATUS_TONE: Record<string, string> = {
  "Closed Won": "bg-success/10 text-success border-success/30",
  "Closed Lost": "bg-destructive/10 text-destructive border-destructive/30",
};

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

function readString(value: unknown): string {
  return typeof value === "string" ? value.trim() : "";
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function looksLikeUuid(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());
}

function displayRef(value: unknown, fallback = "—"): string {
  const text = typeof value === "string" ? value.trim() : "";
  if (!text) return fallback;
  return looksLikeUuid(text) ? fallback : text;
}

export function OpportunityReadOnlySummary({ opportunity, noJobReason, embedded = false }: Props) {
  const [photoUrls, setPhotoUrls] = useState<string[]>([]);
  const isWon = opportunity.stage === "Closed Won";
  const isLost = opportunity.stage === "Closed Lost";
  const StageIcon = isWon ? Trophy : Ban;
  const customerName = opportunity.customerName || opportunity.primaryName || "Unnamed";

  // Snapshot value-at-close — purely a stored field on the opportunity.
  // Not a canonical pricing computation. Labeled as snapshot for audit clarity.
  const snapshotValue = opportunity.totalEstimate || 0;
  const fullAddress = [
    opportunity.address,
    opportunity.city,
    [opportunity.state, opportunity.zip].filter(Boolean).join(" ").trim(),
  ].filter(Boolean).join(", ");
  const displayRep = resolveProfileDisplayName(opportunity.assignedRep, undefined, "Unassigned");
  const stageCount = opportunity.stageHistory?.length ?? 0;
  const activityCount = opportunity.activities?.length ?? 0;
  const hasContract = Boolean(opportunity.contract);
  const hasDeposit = Boolean(opportunity.deposit);
  const leadRef = String(opportunity.leadId ?? "").trim();
  const quoteRef = String(opportunity.quoteId ?? opportunity.quote_number ?? "").trim();
  const displayOpportunityRef = String(opportunity.displayReference || opportunity.id || "Opportunity").trim();
  const normalizedWonOutcome =
    opportunity.wonOutcome ??
    (isWon
      ? (noJobReason === "canceled"
          ? "canceled"
          : noJobReason === "finance_declined"
            ? "finance_declined"
            : noJobReason === "no_link"
              ? "pending"
              : (opportunity.linkedJobId || opportunity.convertedJobId) ? "net" : "pending")
      : undefined);
  const config = asRecord(opportunity.projectConfig);
  const pricingFromConfig = asRecord(config?.pricing);
  const subtotal =
    readNumber((opportunity as unknown as { subtotal?: unknown }).subtotal) ??
    readNumber(pricingFromConfig?.subtotal) ??
    readNumber(config?.subtotal);
  const tax =
    readNumber((opportunity as unknown as { tax?: unknown }).tax) ??
    readNumber(pricingFromConfig?.tax) ??
    readNumber(config?.tax);
  const discount =
    readNumber((opportunity as unknown as { discount?: unknown }).discount) ??
    readNumber(pricingFromConfig?.discount) ??
    readNumber(config?.discount);
  const total =
    readNumber((opportunity as unknown as { total?: unknown }).total) ??
    readNumber(pricingFromConfig?.total) ??
    (subtotal != null ? subtotal + (tax ?? 0) - (discount ?? 0) : null) ??
    (snapshotValue > 0 ? snapshotValue : null);
  const projectCategory =
    readString(config?.projectCategory) ||
    readString(config?.consultationCategory) ||
    readString(config?.autoProjectType) ||
    readString(config?.bathroomSystemType);
  const selectedBuildType =
    readString(config?.selectedBuildType) ||
    readString(config?.selectedSystemType) ||
    readString(config?.selectedShowerType);
  const selectedDimensions =
    readString(config?.selectedDimensions) ||
    readString(config?.selectedSize) ||
    readString(config?.dimensions);
  const selectedMaterial =
    readString(config?.selectedWallColor) ||
    readString(config?.selectedWallType) ||
    readString(config?.selectedMaterial);
  const selectedBase =
    readString(config?.selectedBaseType) ||
    readString(config?.selectedPanType) ||
    readString(config?.selectedBase);
  const selectedGlass =
    readString(config?.selectedDoorType) ||
    readString(config?.selectedGlassType) ||
    readString(config?.selectedEnclosure);
  const addons = Array.isArray(config?.selectedAddOns)
    ? (config?.selectedAddOns as unknown[])
    : Array.isArray(config?.selectedAccessories)
      ? (config?.selectedAccessories as unknown[])
      : [];
  const quoteId = typeof opportunity.quoteId === "string" ? opportunity.quoteId.trim() : "";

  useEffect(() => {
    let isActive = true;
    const loadPhotos = async () => {
      if (!quoteId || !looksLikeUuid(quoteId)) {
        setPhotoUrls([]);
        return;
      }
      try {
        const records = await getAttachments("quote", quoteId);
        const photos = records.filter((r: any) => (r.attachmentType ?? r.attachment_type) === "photo");
        const normalizeStoragePath = (rawPath: string, bucket: string): string => {
          if (!rawPath) return "";
          const privateMarker = `/storage/v1/object/sign/${bucket}/`;
          const publicMarker = `/storage/v1/object/public/${bucket}/`;
          if (rawPath.includes(privateMarker)) return rawPath.split(privateMarker)[1]?.split("?")[0] ?? "";
          if (rawPath.includes(publicMarker)) return rawPath.split(publicMarker)[1]?.split("?")[0] ?? "";
          return rawPath;
        };
        const signed = await Promise.all(
          photos.map((r: any) => {
            const rawPath = String(r.filePath ?? r.file_path ?? "");
            const storagePath = normalizeStoragePath(rawPath, "job-documents");
            return createSignedAttachmentUrl(storagePath, "job-documents").catch(() => rawPath);
          })
        );
        if (!isActive) return;
        const unique = Array.from(new Set(signed.filter(Boolean)));
        setPhotoUrls(unique);
      } catch {
        if (isActive) setPhotoUrls([]);
      }
    };
    void loadPhotos();
    return () => {
      isActive = false;
    };
  }, [quoteId]);

  return (
    <div className={cn("space-y-4", embedded ? "max-w-none" : "max-w-3xl")}>
      {/* Back link */}
      {!embedded && (
        <Link
          to="/opportunities"
          className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Opportunities
        </Link>
      )}

      {/* Read-only banner */}
      <div className="flex items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
        <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
        <p>
          Read-only opportunity summary. This record is closed and is no longer a
          live workspace. All values shown are audit snapshots captured at close;
          they are not computed from canonical sources.
        </p>
      </div>

      {/* Header card */}
      <Card className="shadow-none">
        <CardContent className="p-5">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-mono font-semibold text-muted-foreground tabular-nums">
                  {displayOpportunityRef}
                </span>
                <span
                  className={cn(
                    "inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[11px] font-semibold",
                    STATUS_TONE[opportunity.stage] || "bg-muted text-muted-foreground border-border",
                  )}
                >
                  <StageIcon className="h-3 w-3" />
                  {opportunity.stage}
                </span>
                {isWon && normalizedWonOutcome && (
                  <Badge variant="outline" className="text-[10px]">
                    {WON_OUTCOME_LABELS[normalizedWonOutcome]}
                  </Badge>
                )}
              </div>
              <h1 className="text-xl sm:text-2xl font-bold tracking-tight mt-1.5 truncate">
                {customerName}
              </h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {opportunity.projectType || "Project type not set"}
                {opportunity.assignedRep && (
                  <>
                    {" · "}
                    <span className="text-foreground/70">{resolveProfileDisplayName(opportunity.assignedRep, undefined, "Unassigned")}</span>
                  </>
                )}
                {opportunity.leadSource && (
                  <>
                    {" · "}
                    <span className="text-foreground/60">{opportunity.leadSource}</span>
                  </>
                )}
              </p>
            </div>

            {snapshotValue > 0 && (
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold">
                  Snapshot · at close
                </p>
                <p className="text-2xl font-bold tabular-nums leading-tight">
                  ${snapshotValue.toLocaleString()}
                </p>
                <p className="text-[10px] text-muted-foreground mt-0.5">
                  Audit only · not canonical
                </p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Outcome card */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-3">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Outcome
          </p>

          {isWon && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Won at</dt>
              <dd className="text-foreground tabular-nums">
                {opportunity.wonAt ? format(new Date(opportunity.wonAt), "PP") : "—"}
              </dd>
              <dt className="text-muted-foreground">Won outcome</dt>
              <dd className="text-foreground">
                {normalizedWonOutcome ? WON_OUTCOME_LABELS[normalizedWonOutcome] : "—"}
              </dd>
              {noJobReason && (
                <>
                  <dt className="text-muted-foreground">No active job</dt>
                  <dd className="text-foreground">
                    {noJobReason === "rescission" && "Rescinded by customer"}
                    {noJobReason === "canceled" && "Canceled before production"}
                    {noJobReason === "finance_declined" && "Finance declined"}
                    {noJobReason === "no_link" && "No linked job on record"}
                  </dd>
                </>
              )}
            </dl>
          )}

          {isLost && (
            <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <dt className="text-muted-foreground">Lost at</dt>
              <dd className="text-foreground tabular-nums">
                {opportunity.lostAt ? format(new Date(opportunity.lostAt), "PP") : "—"}
              </dd>
              <dt className="text-muted-foreground">Reason</dt>
              <dd className="text-foreground">{opportunity.lostReason || "—"}</dd>
            </dl>
          )}
        </CardContent>
      </Card>

      {/* Record metadata */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Record
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Created</dt>
            <dd className="text-foreground tabular-nums">
              {opportunity.createdAt ? format(new Date(opportunity.createdAt), "PP") : "—"}
            </dd>
            <dt className="text-muted-foreground">Updated</dt>
            <dd className="text-foreground tabular-nums">
              {opportunity.updatedAt ? format(new Date(opportunity.updatedAt), "PP") : "—"}
            </dd>
            {(opportunity.customerAddress || fullAddress) && (
              <>
                <dt className="text-muted-foreground">Address</dt>
                <dd className="text-foreground truncate">{opportunity.customerAddress || fullAddress}</dd>
              </>
            )}
          </dl>
        </CardContent>
      </Card>

      {/* Contact + ownership snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Contact & Ownership Snapshot
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Primary phone</dt>
            <dd className="text-foreground">{opportunity.primaryPhone || opportunity.customerPhone || "—"}</dd>
            <dt className="text-muted-foreground">Primary email</dt>
            <dd className="text-foreground truncate">{opportunity.primaryEmail || opportunity.customerEmail || "—"}</dd>
            <dt className="text-muted-foreground">Assigned rep</dt>
            <dd className="text-foreground">{displayRep}</dd>
            <dt className="text-muted-foreground">Lead source</dt>
            <dd className="text-foreground">{opportunity.leadSource || "—"}</dd>
          </dl>
        </CardContent>
      </Card>

      {/* Lifecycle + linkage snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Lifecycle & Linkage Snapshot
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Display reference</dt>
            <dd className="text-foreground">{opportunity.displayReference || "—"}</dd>
            <dt className="text-muted-foreground">Quote reference</dt>
            <dd className="text-foreground">{quoteRef || "—"}</dd>
            <dt className="text-muted-foreground">Lead reference</dt>
            <dd className="text-foreground">{leadRef || "—"}</dd>
            <dt className="text-muted-foreground">Linked job id</dt>
            <dd className="text-foreground">{opportunity.linkedJobId || opportunity.convertedJobId || "—"}</dd>
            <dt className="text-muted-foreground">Stage events</dt>
            <dd className="text-foreground">{stageCount}</dd>
            <dt className="text-muted-foreground">Activity events</dt>
            <dd className="text-foreground">{activityCount}</dd>
          </dl>
        </CardContent>
      </Card>

      {/* Commercial snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Commercial Snapshot
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Total estimate</dt>
            <dd className="text-foreground tabular-nums">${(opportunity.totalEstimate || 0).toLocaleString()}</dd>
            <dt className="text-muted-foreground">Pricing status</dt>
            <dd className="text-foreground">{opportunity.pricingStatus || "—"}</dd>
            <dt className="text-muted-foreground">Contract snapshot</dt>
            <dd className="text-foreground">{hasContract ? "Present" : "—"}</dd>
            <dt className="text-muted-foreground">Deposit snapshot</dt>
            <dd className="text-foreground">{hasDeposit ? "Present" : "—"}</dd>
            <dt className="text-muted-foreground">Presented at</dt>
            <dd className="text-foreground tabular-nums">
              {opportunity.presentedAt ? format(new Date(opportunity.presentedAt), "PP") : "—"}
            </dd>
            <dt className="text-muted-foreground">Locked at</dt>
            <dd className="text-foreground tabular-nums">
              {opportunity.lockedAt ? format(new Date(opportunity.lockedAt), "PP") : "—"}
            </dd>
          </dl>
        </CardContent>
      </Card>

      {/* Price breakdown snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Price Breakdown Snapshot
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Subtotal</dt>
            <dd className="text-foreground tabular-nums">{subtotal != null ? `$${subtotal.toLocaleString()}` : "—"}</dd>
            <dt className="text-muted-foreground">Tax</dt>
            <dd className="text-foreground tabular-nums">{tax != null ? `$${tax.toLocaleString()}` : "—"}</dd>
            <dt className="text-muted-foreground">Discount</dt>
            <dd className="text-foreground tabular-nums">{discount != null ? `$${discount.toLocaleString()}` : "—"}</dd>
            <dt className="text-muted-foreground">Total</dt>
            <dd className="text-foreground tabular-nums font-semibold">{total != null ? `$${total.toLocaleString()}` : "—"}</dd>
          </dl>
        </CardContent>
      </Card>

      {/* Project config snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Project Config Snapshot
          </p>
          <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
            <dt className="text-muted-foreground">Category</dt>
            <dd className="text-foreground">{projectCategory || "—"}</dd>
            <dt className="text-muted-foreground">Build type</dt>
            <dd className="text-foreground">{selectedBuildType || "—"}</dd>
            <dt className="text-muted-foreground">Dimensions</dt>
            <dd className="text-foreground">{selectedDimensions || "—"}</dd>
            <dt className="text-muted-foreground">Wall / material</dt>
            <dd className="text-foreground">{selectedMaterial || "—"}</dd>
            <dt className="text-muted-foreground">Base / pan</dt>
            <dd className="text-foreground">{selectedBase || "—"}</dd>
            <dt className="text-muted-foreground">Glass / enclosure</dt>
            <dd className="text-foreground">{selectedGlass || "—"}</dd>
            <dt className="text-muted-foreground">Add-ons</dt>
            <dd className="text-foreground">{addons.length > 0 ? `${addons.length} selected` : "—"}</dd>
          </dl>
          {opportunity.scopeOfWork && (
            <div className="pt-2 border-t">
              <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground mb-1">Scope of work</p>
              <p className="text-sm text-foreground whitespace-pre-wrap">{opportunity.scopeOfWork}</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Photos snapshot */}
      <Card className="shadow-none">
        <CardContent className="p-5 space-y-2">
          <p className="text-[10px] uppercase tracking-wide font-semibold text-muted-foreground">
            Photos Snapshot
          </p>
          {photoUrls.length === 0 ? (
            <p className="text-sm text-muted-foreground">—</p>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {photoUrls.map((url, index) => (
                <a key={`${url}-${index}`} href={url} target="_blank" rel="noreferrer">
                  <StorageThumbnailImage
                    src={url}
                    alt={`Opportunity snapshot ${index + 1}`}
                    className="h-28 w-full rounded-md border border-border object-cover"
                  />
                </a>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
